import React from 'react';
import { X, Calendar, DollarSign, TrendingUp, TrendingDown, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Button from '../../../components/ui/Button';

const CalculationDetailModal = ({ calculation, onClose }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value || 0);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const isProfit = calculation?.lucroLiquido > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isProfit ? 'bg-success/10' : 'bg-destructive/10'}`}>
              {isProfit ? (
                <TrendingUp className={`h-6 w-6 ${isProfit ? 'text-success' : 'text-destructive'}`} />
              ) : (
                <TrendingDown className={`h-6 w-6 ${isProfit ? 'text-success' : 'text-destructive'}`} />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">
                {calculation?.projectName || 'Cálculo de Lucro'}
              </h2>
              <p className="text-sm text-muted-foreground">
                Criado em {formatDate(calculation?.createdAt)}
              </p>
            </div>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            className="h-8 w-8"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Result Summary */}
          <div className={`p-6 rounded-lg ${isProfit ? 'bg-success/10 border border-success/20' : 'bg-destructive/10 border border-destructive/20'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Resultado Final</p>
                <p className={`text-3xl font-bold ${isProfit ? 'text-success' : 'text-destructive'}`}>
                  {formatCurrency(calculation?.lucroLiquido)}
                </p>
                <p className={`text-sm font-medium mt-1 ${isProfit ? 'text-success' : 'text-destructive'}`}>
                  {isProfit ? 'Lucro' : 'Prejuízo'} de {calculation?.lucroPercent?.toFixed(2)}%
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground mb-1">Receita Total</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(calculation?.totalObra)}
                </p>
              </div>
            </div>
          </div>

          {/* Receita Section */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Receita
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Total da Obra</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(calculation?.totalObra)}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Já Faturado</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(calculation?.valorJaFaturado)}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">A Faturar</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(calculation?.valorAFaturar)}</p>
              </div>
            </div>
          </div>

          {/* Custos Diretos */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Custos Diretos
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Empreiteiro</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(calculation?.custoEmpreiteiro)}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Visitas à Obra</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(calculation?.visitasObraTotal)}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Outros Custos</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(calculation?.outrosCustosDiretos)}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg border-2 border-primary/20">
                <p className="text-xs text-muted-foreground mb-1">Total Custos Diretos</p>
                <p className="text-lg font-bold text-primary">{formatCurrency(calculation?.custoDiretoTotal)}</p>
              </div>
            </div>
          </div>

          {/* Custos Mensais */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Custos Mensais e Duração
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Equipe Mensal</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(calculation?.equipeMensalPrestadores)}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Fixos Mensais</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(calculation?.fixosMensaisEscritorio)}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Duração</p>
                <p className="text-lg font-semibold text-foreground">{calculation?.duracaoMeses} meses ({calculation?.duracaoSemanas} semanas)</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg border-2 border-primary/20">
                <p className="text-xs text-muted-foreground mb-1">Total Equipe</p>
                <p className="text-lg font-bold text-primary">{formatCurrency(calculation?.custoEquipeTotal)}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg border-2 border-primary/20">
                <p className="text-xs text-muted-foreground mb-1">Total Fixos</p>
                <p className="text-lg font-bold text-primary">{formatCurrency(calculation?.custoFixosTotal)}</p>
              </div>
            </div>
          </div>

          {/* Impostos */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Impostos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">DAS ({calculation?.dasPercentualEfetivo}%)</p>
                <p className="text-sm text-muted-foreground mb-2">Base: {calculation?.dasBaseCalculo === 'total_obra' ? 'Total da Obra' : 'Valor a Faturar'}</p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(calculation?.dasTotal)}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">DARF Total ({calculation?.duracaoMeses} meses)</p>
                <p className="text-sm text-muted-foreground mb-2">
                  INSS: {formatCurrency(calculation?.inssMensal)}/mês | IRRF: {formatCurrency(calculation?.irrfMensal)}/mês
                </p>
                <p className="text-lg font-semibold text-foreground">{formatCurrency(calculation?.darfTotal)}</p>
              </div>
            </div>
          </div>

          {/* Total Gastos */}
          <div className="bg-muted p-6 rounded-lg border-2 border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total de Gastos</p>
                <p className="text-xs text-muted-foreground">
                  Custos Diretos + Equipe + Fixos + Impostos
                </p>
              </div>
              <p className="text-2xl font-bold text-foreground">
                {formatCurrency(calculation?.totalGastos)}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex justify-end">
          <Button onClick={onClose} variant="outline">
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CalculationDetailModal;